import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../Services/authentication.service';
import { isAuthSuccess } from '../Models/Auth.model';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const token = localStorage.getItem('accessToken');

    const isApiRequest = req.url.includes(environment.backendUrl) || req.url.includes('localhost:5196');
    const hasAuthorizationHeader = req.headers.has('Authorization');
    const isAuthEndpoint = req.url.includes('/auth/login') ||
        req.url.includes('/auth/azure/login') ||
        req.url.includes('/auth/refresh') ||
        req.url.includes('/auth/verify-mfa') ||
        req.url.includes('/auth/enable-mfa');
    if (!token || !isApiRequest || isAuthEndpoint || hasAuthorizationHeader) {
        return next(req);
    }

    const authReq = addToken(req, token);

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
                return handle401Error(req, next, authService, router);
            }
            return throwError(() => error);
        })
    );
};

function addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`
        }
    });
}

function handle401Error(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    router: Router
) {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        const refreshToken = authService.getRefreshToken();

        if (!refreshToken) {
            isRefreshing = false;
            authService.logout();
            return throwError(() => new Error('No refresh token'));
        }

        return authService.refreshToken().pipe(
            switchMap(response => {
                isRefreshing = false;

                if (isAuthSuccess(response)) {
                    refreshTokenSubject.next(response.accessToken);
                    return next(addToken(req, response.accessToken));
                }

                authService.logout();
                return throwError(() => new Error('Refresh failed'));
            }),
            catchError(err => {
                isRefreshing = false;
                authService.logout();
                return throwError(() => err);
            })
        );
    }

    // Andere Requests warten auf Refresh
    return refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next(addToken(req, token!)))
    );
};